import React from 'react';
import { Wind } from 'lucide-react';
import { DailySoaringForecast } from '@/lib/mockWeatherData';
import { useSettings } from '@/context/SettingsContext';

interface WindProfileTableProps {
  forecast: DailySoaringForecast;
}

function windStrengthColor(kmh: number): string {
  if (kmh < 28) return '#34d399';
  if (kmh < 46) return '#fbbf24';
  if (kmh < 65) return '#f87171';
  return '#a78bfa';
}

function windStrengthLabel(kmh: number): string {
  if (kmh < 28) return 'Calme';
  if (kmh < 46) return 'Modéré';
  if (kmh < 65) return 'Fort';
  return 'Très fort';
}

export default function WindProfileTable({ forecast }: WindProfileTableProps) {
  const { formatWind } = useSettings();

  const levels = [
    { label: 'Sol', alt: '0 m', data: forecast.windSurface },
    { label: '2000 ft', alt: '610 m', data: forecast.windAloft2000 },
    { label: '5000 ft', alt: '1524 m', data: forecast.windAloft5000 },
  ];

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Wind size={14} style={{ color: 'var(--info-blue)' }} />
        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Profil de Vent</h3>
      </div>
      <div className="space-y-2">
        {levels.map((lvl) => {
          const fmt = formatWind(lvl.data.speed);
          return (
            <div
              key={`wind-${lvl.label}`}
              className="flex items-center justify-between px-3 py-2.5 rounded-lg"
              style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--card-border)' }}
            >
              <div>
                <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{lvl.label}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{lvl.alt}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="font-mono-data text-sm font-bold" style={{ color: windStrengthColor(lvl.data.speed) }}>
                    {fmt.primary} {fmt.primaryUnit}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{fmt.secondary} {fmt.secondaryUnit} · {lvl.data.dir}</p>
                </div>
                <span
                  className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{
                    backgroundColor: `${windStrengthColor(lvl.data.speed)}20`,
                    color: windStrengthColor(lvl.data.speed),
                  }}
                >
                  {windStrengthLabel(lvl.data.speed)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
